/**
 * Buffer-series grouping "brain" for the /products catalog.
 *
 * Pure grouping logic — no React, no live-data fetches. Buffer funds are issued
 * as a *family* of monthly "outcome period" variants (a May fund, a June fund,
 * …) that all share one `seriesSlug` (e.g. "buffer-tech15"). On the catalog we
 * collapse each family into ONE card with a month dropdown instead of showing
 * every monthly fund as its own card.
 *
 * Despite the misleading `// e.g. "june-series"` comment on `Fund.seriesSlug`,
 * real DDB values are FAMILY slugs ("buffer-tech15", "buffer-useq30", …), not
 * month-based. We bucket by `seriesSlug` (falling back to the fund `id` so a
 * mis-tagged fund still renders as its own one-fund "series").
 */
import type { Fund } from "@/data/funds";

/**
 * Calendar display order for a buffer family's months: the outcome year starts
 * in May, so May → Apr (with Jan–Apr treated as the *following* calendar year).
 * Keys are lowercase 3-char month prefixes (matches `bufferMonthKey`).
 */
export const BUFFER_MONTH_ORDER = [
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
  "jan",
  "feb",
  "mar",
  "apr",
] as const;

/**
 * Normalize a `bufferConfig.seriesMonth` value ("May", "June", "June 2026", …)
 * to its lowercase 3-char key ("may", "jun"). Returns "" for null/blank so the
 * caller can treat it as "unknown" and sort it last.
 */
export function bufferMonthKey(seriesMonth: string | null | undefined): string {
  return (seriesMonth ?? "").trim().slice(0, 3).toLowerCase();
}

/** Calendar sort index for a fund within its family; unknown months sort last. */
function bufferMonthIndex(fund: Fund): number {
  const idx = BUFFER_MONTH_ORDER.indexOf(
    bufferMonthKey(fund.bufferConfig?.seriesMonth) as (typeof BUFFER_MONTH_ORDER)[number],
  );
  return idx === -1 ? BUFFER_MONTH_ORDER.length : idx;
}

/** One buffer family: the grouping key plus its monthly funds (always ≥ 1). */
export interface BufferSeriesGroup {
  seriesSlug: string;
  funds: [Fund, ...Fund[]];
}

/**
 * Group buffer funds into one entry per family.
 *
 * - Bucket key = `fund.seriesSlug ?? fund.id` (family slug; id fallback so an
 *   untagged buffer fund still renders as its own single-fund series).
 * - SERIES ORDER = order of first encounter in the input. The caller passes
 *   AUM-descending-sorted buffer funds, so families end up ordered by their
 *   highest-AUM month — the input order is preserved verbatim.
 * - WITHIN each family, funds sort by calendar month (May → Apr) via
 *   `bufferConfig.seriesMonth`; months that can't be parsed sort last.
 *
 * Nothing is omitted — every input fund lands in exactly one group.
 */
export function groupBufferBySeries(funds: Fund[]): BufferSeriesGroup[] {
  const order: string[] = [];
  const byKey = new Map<string, Fund[]>();

  for (const fund of funds) {
    const key = fund.seriesSlug ?? fund.id;
    const existing = byKey.get(key);
    if (existing) {
      existing.push(fund);
    } else {
      byKey.set(key, [fund]);
      order.push(key);
    }
  }

  return order.map((key) => {
    // Non-null: every key in `order` was inserted into `byKey` with ≥ 1 fund.
    const groupFunds = byKey.get(key) as [Fund, ...Fund[]];
    groupFunds.sort((a, b) => bufferMonthIndex(a) - bufferMonthIndex(b));
    return { seriesSlug: key, funds: groupFunds };
  });
}
